import { supabase } from './supabaseClient.js';

const CHAT_API_URL = '/api/chat';

// The client always OFFERS streaming; whether it actually streams is decided
// server-side at runtime (STREAM_CHAT env in api/chat.js) and detected here from
// the response content-type. This avoids Vite's build-time env inlining, which
// made a VITE_ flag silently no-op unless the bundle was rebuilt with it set.

export async function callAI({
  trackId,
  conversationHistory = [],
  userInput,
  statePayload     = null,
  lessonData       = null,
  isProfileSession = false,
  effectiveMode    = null,
  behaviorState        = null,
  conversationSummary  = null,
  activePattern        = null,
  learnerId            = null,
  sessionId            = null,
  sessionPhase         = null,
  attemptHistory       = [],
  turnDirective        = null,
  visualIntent         = false,
  isInternalAdvance    = false,
  onToken              = null,   // (deltaText, fullSoFar) — called per chunk when streaming
}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const response = await fetch(CHAT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      trackId,
      conversationHistory,
      userInput,
      statePayload,
      lessonData,
      isProfileSession,
      effectiveMode,
      behaviorState,
      conversationSummary,
      activePattern,
      learnerId,
      sessionId,
      sessionPhase,
      attemptHistory,
      turnDirective,
      visualIntent,
      isInternalAdvance,
      stream: true,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    // Any 402 is the server-side paywall signal — free limit reached
    // (trial_exhausted) OR a lapsed/canceled subscription (subscription_inactive).
    // Dispatch a global event so any mounted overlay renders the hard paywall,
    // and throw a typed error so upstream handlers branch deterministically.
    if (response.status === 402) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('adaptos:trial_exhausted'));
      }
      const e = new Error(err?.error || 'trial_exhausted');
      e.code = 'trial_exhausted';
      throw e;
    }
    throw new Error(err?.error || `Chat API error ${response.status}`);
  }

  // Streaming path — read the raw text body chunk-by-chunk, surfacing deltas via
  // onToken for incremental render. resolvedDirective rides in a header; the full
  // text is what the engine parses for artifacts once the stream completes.
  const isStream = !!response.body &&
    (response.headers.get('content-type') || '').startsWith('text/plain');
  if (isStream) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let content = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (chunk) { content += chunk; onToken?.(chunk, content); }
    }
    if (!content) throw new Error('Empty response from GPT');
    return { content, resolvedDirective: response.headers.get('X-Resolved-Directive') || null, truncated: false };
  }

  const data = await response.json();
  if (!data?.content) throw new Error('Empty response from GPT');
  if (data.truncated) console.warn('[aiService] Truncated response received — quiz JSON may be incomplete');
  return { content: data.content, resolvedDirective: data.resolvedDirective ?? null, truncated: !!data.truncated };
}
